/**
 * /suprimentos/produtos — Cadastro de Suprimentos (redesign 2026-09-15).
 *
 * Mesma linguagem da fila de Requisições de Compra (ADR-0069): cabeçalho +
 * KPIs acionáveis num bloco só, busca com atalho "/", botão Filtros (as oito
 * dimensões do padrão Excel continuam, agora num slide-over), chips rápidos,
 * segmentado Lista | Por tipo, tabela com coluna Tipo, barra saldo × mínimo
 * e último preço, seleção em massa e painel de detalhe com abas.
 *
 * A lista inteira vem numa carga só e os filtros rodam no navegador: é o
 * que o Excel faz, e com o catálogo da cliente-piloto (< 2 mil itens) é instantâneo.
 * Formulário em `FormularioProduto`; colunas escolhidas em `ColunasPanel`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiColumns, FiPlus, FiRefreshCw, FiSettings, FiUpload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import ConfirmDialog from "../../../components/ConfirmDialog";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";
import { useTabelaPreferenciaUI } from "../../../hooks/useTabelaPreferencia";
import { vincularFornecedorLote } from "../../../services/compras";
import {
  aplicarTextura, atualizarProduto, atualizarProdutosLote, criarProduto, deletarProduto, desativarProdutos, excluirProdutos,
  listarDepositos, listarProdutos, listarTiposSuprimento,
} from "../../../services/estoque";
import { unidadeDefault } from "../../../services/unidades";
import { plural } from "../../../utils/dinheiro";
import { Carregando, Vazio, VazioFiltro } from "../_shared/Estados";
import { exportarProdutos } from "../_shared/exportarSuprimentos";
import { ErrorMsg } from "../styles";
import BarraMassa, { ModalLote } from "./BarraMassa";
import { COLUNA_POR_KEY, normalizarColunas } from "./colunas";
import ColunasPanel from "./ColunasPanel";
import { infoSaldo, ordenarPorCriticidade, semFornecedor, semGiro, semPreco, valorEstoque } from "./derivados";
import {
  DIMENSOES, RAPIDO_POR_ID, SELECAO_VAZIA, aplicarFiltros, contagensRapidas, contarDimensoesAtivas, opcoesDe, resumoChip,
} from "./filtros";
import FiltrosPanel from "./FiltrosPanel";
import FormularioProduto, { inteiroTexto } from "./FormularioProduto";
import KpisCadastro from "./KpisCadastro";
import ProdutoDetalhePanel from "./ProdutoDetalhePanel";
import TabelaSuprimentos from "./TabelaSuprimentos";
import { ChipsRapidos, Toolbar } from "./Toolbar";

/** Chave da preferência de tabela (por usuário, no backend). */
const CHAVE_COLUNAS = "suprimentos_produtos_colunas";
const DEBOUNCE_BUSCA_MS = 300;

/** Registro do backend -> estado do formulário (medidas inteiras, dinheiro numérico). */
export function paraFormulario(p) {
  return {
    ...p,
    custo_medio: p.custo_medio === null || p.custo_medio === undefined ? null : Number(p.custo_medio),
    largura_mm: inteiroTexto(p.largura_mm),
    comprimento_mm: inteiroTexto(p.comprimento_mm),
    espessura_mm: inteiroTexto(p.espessura_mm),
    atributos: p.atributos || {},
  };
}

const NOVO = {
  codigo: "", nome: "", descricao: "", unidade: "UN", categoria: "OUTRO",
  custo_medio: null, estoque_minimo: "0", ativo: true, compravel: true, m2_por_unidade: "",
  largura_mm: "", comprimento_mm: "", espessura_mm: "", tem_veio: null,
  tipo_suprimento: "", atributos: {}, fabricante: "", referencia_fabricante: "", ncm: "",
};

function linhasDe(payload) {
  return Array.isArray(payload) ? payload : payload?.results || [];
}

function Produtos() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const mobile = useMediaQuery("(max-width: 768px)");
  const [unidade, setUnidade] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [buscaInput, setBuscaInput] = useState("");
  const [busca, setBusca] = useState("");
  const [selecao, setSelecao] = useState(SELECAO_VAZIA);
  const [rapido, setRapido] = useState(null);
  const { filtros: persistidos, setFiltros: setPersistidos } = usePersistedFilters("suprimentos:produtos", { vista: "lista" });
  const vista = persistidos.vista === "tipo" ? "tipo" : "lista";
  const setVista = (v) => setPersistidos((p) => ({ ...p, vista: v }));
  const [editando, setEditando] = useState(null);
  const [painelColunas, setPainelColunas] = useState(false);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [apagando, setApagando] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [versaoDetalhe, setVersaoDetalhe] = useState(0);
  // Seleção em massa: ids marcados; "todos" = os visíveis no filtro.
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [confirmarLote, setConfirmarLote] = useState(false);
  // Ação em massa aberta no modal (categoria | minimo | fornecedor) e execução.
  const [acaoLote, setAcaoLote] = useState(null);
  const [executandoLote, setExecutandoLote] = useState(false);
  // Recusados na exclusão (em uso): a tela oferece desativar no lugar.
  const [recusados, setRecusados] = useState(null);
  // Catálogo de tipos servido pelo backend (ADR-0061 D1). Vem de lá e não
  // daqui de propósito: duplicar a lista no frontend foi como o catálogo
  // de MDF virou uma segunda verdade.
  const [tiposCatalogo, setTiposCatalogo] = useState([]);
  const [nDepositos, setNDepositos] = useState(0);
  const buscaRef = useRef(null);

  const podeCriar = isMaster || hasAllPermissions(["estoque:create_produto"]);
  const podeEditar = isMaster || hasAllPermissions(["estoque:edit_produto"]);
  const podeDeletar = isMaster || hasAllPermissions(["estoque:delete_produto"]);
  const podeFornecedor = isMaster || hasAllPermissions(["compras:gerenciar_fornecedor"]);

  // ----- colunas escolhidas pelo usuário -----
  const { getUI, setUI, flush, estado: estadoPref } = useTabelaPreferenciaUI();
  const [colunas, setColunas] = useState(normalizarColunas(null));
  const hidratou = useRef(false);
  useEffect(() => {
    // Lê por getUI() (cache do provider): o `ui` de estado chega um render
    // depois de `estado` virar 'pronto' e a preferência nunca seria aplicada.
    if (hidratou.current || estadoPref === "loading") return;
    hidratou.current = true;
    if (estadoPref !== "pronto") return;
    const salvas = getUI()?.[CHAVE_COLUNAS];
    if (salvas) setColunas(normalizarColunas(salvas));
  }, [estadoPref, getUI]);

  const salvarColunas = async (keys) => {
    setColunas(keys);
    setPainelColunas(false);
    setUI({ [CHAVE_COLUNAS]: keys });
    // flush explícito: sem ele o PUT só sairia no debounce de 2s.
    await flush({ propagarErro: false });
  };

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput.trim()), DEBOUNCE_BUSCA_MS);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // Atalho "/" foca a busca.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        buscaRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    unidadeDefault(request).then(setUnidade);
    listarTiposSuprimento(request)
      .then((r) => setTiposCatalogo(r?.tipos || []))
      .catch(() => setTiposCatalogo([]));
    listarDepositos(request, { ativo: true })
      .then((r) => setNDepositos(linhasDe(r).length))
      .catch(() => setNDepositos(0));
  }, [request]);

  const tipoAtual = useMemo(
    () => tiposCatalogo.find((t) => t.codigo === editando?.tipo_suprimento),
    [tiposCatalogo, editando?.tipo_suprimento],
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listarProdutos(request, { pageSize: 2000 });
      setProdutos(linhasDe(payload));
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar produtos.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  // ----- filtragem -----
  const ctx = useMemo(() => ({ tipos: tiposCatalogo }), [tiposCatalogo]);

  const recorte = useMemo(
    () => aplicarFiltros(produtos, { busca, selecao }, ctx),
    [produtos, busca, selecao, ctx],
  );
  const filtrados = useMemo(
    () => ordenarPorCriticidade(rapido ? aplicarFiltros(recorte, { busca: "", selecao: SELECAO_VAZIA, rapido }, ctx) : recorte),
    [recorte, rapido, ctx],
  );
  const opcoes = useMemo(
    () => Object.fromEntries(DIMENSOES.map((d) => [d.key, opcoesDe(produtos, d, ctx)])),
    [produtos, ctx],
  );
  const contagens = useMemo(() => ({ total: recorte.length, ...contagensRapidas(recorte, ctx) }), [recorte, ctx]);
  const nDimensoes = contarDimensoesAtivas(selecao);
  const temFiltro = Boolean(busca) || nDimensoes > 0 || Boolean(rapido);
  const limparFiltros = () => { setBuscaInput(""); setBusca(""); setSelecao(SELECAO_VAZIA); setRapido(null); };

  const resumoFiltro = useMemo(() => {
    const partes = [];
    if (busca) partes.push(`busca "${busca}"`);
    DIMENSOES.filter((d) => selecao[d.key]).forEach((d) => partes.push(`${d.rotulo} = ${resumoChip(selecao[d.key], 2)}`));
    if (rapido) partes.push(RAPIDO_POR_ID[rapido].label);
    return partes.join(" · ");
  }, [busca, selecao, rapido]);

  // ----- KPIs (sobre o recorte das dimensões, sem o filtro rápido) -----
  const resumo = useMemo(() => {
    const r = { ativos: 0, inativos: 0, abaixo: 0, valorRepor: 0, semPreco: 0, semFornecedor: 0, valorEstoque: 0, semGiro: 0, valorParado: 0 };
    recorte.forEach((p) => {
      if (!p.ativo) { r.inativos += 1; return; }
      r.ativos += 1;
      const s = infoSaldo(p);
      if (s.abaixo) { r.abaixo += 1; r.valorRepor += Math.max(0, s.min - s.saldo) * Number(p.custo_medio || 0); }
      if (semPreco(p)) r.semPreco += 1;
      if (semFornecedor(p)) r.semFornecedor += 1;
      r.valorEstoque += valorEstoque(p);
      if (semGiro(p)) { r.semGiro += 1; r.valorParado += valorEstoque(p); }
    });
    return { ...r, depositos: nDepositos };
  }, [recorte, nDepositos]);

  // ----- seleção em massa -----
  // A seleção só vale para o que está na tela: mudou o filtro, o que saiu de
  // vista sai da seleção (ninguém apaga o que não está vendo).
  const idsVisiveis = useMemo(() => new Set(filtrados.map((p) => p.id)), [filtrados]);
  useEffect(() => {
    setSelecionados((atual) => {
      const mantidos = [...atual].filter((id) => idsVisiveis.has(id));
      return mantidos.length === atual.size ? atual : new Set(mantidos);
    });
  }, [idsVisiveis]);
  const toggleUm = (id) => setSelecionados((atual) => {
    const novo = new Set(atual);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });
  const toggleVarios = (ids, marcar) => setSelecionados((atual) => {
    const novo = new Set(atual);
    ids.forEach((id) => { if (marcar) novo.add(id); else novo.delete(id); });
    return novo;
  });

  const excluirSelecionados = async () => {
    const ids = [...selecionados];
    setConfirmarLote(false);
    try {
      const r = await excluirProdutos(request, ids);
      if (r.apagados) toast.success(`${r.apagados} suprimento(s) apagado(s).`);
      setSelecionados(new Set());
      await carregar();
      // Em uso não se apaga: oferece desativar (decisão do dono, 11/09).
      if (r.recusados.length) setRecusados(r.recusados);
      else if (!r.apagados) toast.error("Nada foi apagado.");
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao apagar.");
    }
  };

  /** Ações em massa: uma chamada de lote no backend (ADR-0070). */
  const executarLote = async (acao, valor) => {
    const ids = [...selecionados];
    setExecutandoLote(true);
    try {
      if (acao === "inativar") {
        const r = await desativarProdutos(request, ids);
        toast.success(`${r.desativados} suprimento(s) inativado(s).`);
      } else if (acao === "fornecedor") {
        const r = await vincularFornecedorLote(request, ids, valor);
        const partes = [];
        if (r.criados) partes.push(`${r.criados} vinculado(s)`);
        if (r.reativados) partes.push(`${r.reativados} reativado(s)`);
        if (r.ja_vinculados) partes.push(`${r.ja_vinculados} já tinha(m)`);
        if (r.recusados?.length) partes.push(`${r.recusados.length} recusado(s)`);
        toast.success(`Fornecedor: ${partes.join(", ") || "nada a fazer"}.`);
      } else {
        const campo = acao === "categoria" ? "categoria" : "estoque_minimo";
        const r = await atualizarProdutosLote(request, ids, campo, valor);
        toast.success(`${acao === "categoria" ? "Categoria" : "Mínimo"}: ${r.atualizados} atualizado(s), ${r.inalterados} já estava(m) assim.`);
      }
      setSelecionados(new Set());
      setAcaoLote(null);
      setVersaoDetalhe((v) => v + 1);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha na ação em massa.");
    } finally {
      setExecutandoLote(false);
    }
  };

  const desativarRecusados = async () => {
    const ids = recusados.map((r) => r.id);
    setRecusados(null);
    try {
      const r = await desativarProdutos(request, ids);
      toast.success(`${r.desativados} suprimento(s) desativado(s).`);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao desativar.");
    }
  };

  // ----- formulário -----
  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      const dados = {
        codigo: editando.codigo,
        nome: editando.nome,
        descricao: editando.descricao || "",
        unidade: editando.unidade,
        categoria: editando.categoria,
        custo_medio: editando.custo_medio === null || editando.custo_medio === undefined ? "0" : String(editando.custo_medio),
        estoque_minimo: editando.estoque_minimo || "0",
        tipo_suprimento: editando.tipo_suprimento || "",
        fabricante: editando.fabricante || "",
        referencia_fabricante: editando.referencia_fabricante || "",
        ncm: editando.ncm || "",
        atributos: editando.atributos || {},
        ativo: editando.ativo ?? true,
        compravel: editando.compravel ?? true,
        m2_por_unidade: editando.m2_por_unidade || null,
        business_unit: editando.business_unit || unidade?.id,
        // Geometria de chapa: inteiros em mm (vazio = não informado).
        largura_mm: editando.largura_mm || null,
        comprimento_mm: editando.comprimento_mm || null,
        espessura_mm: editando.espessura_mm || null,
        tem_veio: editando.tem_veio ?? null,
      };
      const salvo = editando.id
        ? await atualizarProduto(request, editando.id, dados)
        : await criarProduto(request, dados);
      try {
        await aplicarTextura(request, editando.id || salvo?.id, editando);
      } catch (errTextura) {
        // O material ja foi salvo: avisa e segue, sem perder o resto.
        toast.error(`Material salvo, mas a textura não foi enviada: ${errTextura?.payload?.detalhe || errTextura?.message || "erro"}`);
      }
      setEditando(null);
      setVersaoDetalhe((v) => v + 1);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao salvar.");
    }
  };

  const confirmarApagar = async () => {
    // Erro do backend (produto em uso, com movimentos...) chega pelo toast
    // automático do request; o diálogo fecha só no sucesso.
    await deletarProduto(request, apagando.id);
    setApagando(null);
    await carregar();
  };

  const abrirEdicao = (p) => setEditando(paraFormulario(p));
  // A textura nao se copia: a copia nasce sem imagem (o arquivo e do original).
  const duplicar = (p) => setEditando({ ...paraFormulario(p), id: undefined, codigo: "", nome: `${p.nome} (cópia)`, created_at: undefined, updated_at: undefined, textura_url: null, textura_miniatura_url: null });
  const inativar = async (p) => {
    try {
      await atualizarProduto(request, p.id, { ativo: false });
      toast.success(`${p.codigo} inativado.`);
      setVersaoDetalhe((v) => v + 1);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao inativar.");
    }
  };

  const exportar = (formato) => exportarProdutos(formato, filtrados, { filtro: temFiltro ? resumoFiltro : "todos" });

  const colunasAtivas = colunas.map((k) => COLUNA_POR_KEY[k]).filter(Boolean);
  const nTipos = useMemo(() => new Set(produtos.map((p) => p.tipo_suprimento).filter(Boolean)).size, [produtos]);
  const catalogo = `${plural(produtos.length, "item", "itens")} em ${plural(nTipos, "tipo", "tipos")}`;
  // No celular o cabeçalho divide a linha com o botão: título e subtítulo curtos.
  const subtitulo = mobile || !unidade ? catalogo : `${unidade.codigo || ""}${unidade.nome ? ` — ${unidade.nome}` : ""} · ${catalogo}`;

  const acoesHeader = [
    { label: "Importar planilha", icon: <FiUpload size={14} />, onClick: () => navigate("/configuracoes/importacoes") },
    { label: "Colunas", icon: <FiColumns size={14} />, onClick: () => setPainelColunas(true) },
    { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
    { label: "Configurar tipos", icon: <FiSettings size={14} />, onClick: () => navigate("/suprimentos/configuracao") },
    ...(mobile ? [{ label: "Exportar Excel", onClick: () => exportar("excel") }] : []),
  ];

  const conteudo = () => {
    if (loading) return <Carregando linhas={8} colunas={colunasAtivas.length + 1} />;
    if (produtos.length === 0) {
      return (
        <Vazio
          titulo="Nenhum suprimento cadastrado"
          descricao="Cadastre o primeiro material ou importe uma planilha em Configurações → Importações."
          acoes={podeCriar ? [{ label: "Novo suprimento", onClick: () => setEditando({ ...NOVO }) }] : []}
        />
      );
    }
    if (filtrados.length === 0) return <VazioFiltro onLimpar={limparFiltros} />;
    return (
      <TabelaSuprimentos
        produtos={filtrados}
        colunas={colunasAtivas}
        ctx={ctx}
        vista={vista}
        mobile={mobile}
        selecionados={selecionados}
        onToggleUm={toggleUm}
        onToggleVarios={toggleVarios}
        onAbrir={setDetalheId}
        onEditar={podeEditar ? abrirEdicao : null}
        onDuplicar={podeCriar ? duplicar : null}
        onMovimentar={(p) => navigate(`/suprimentos/movimentos?produto=${p.id}`)}
        onInativar={podeEditar ? inativar : null}
        onApagar={podeDeletar ? setApagando : null}
        podeSelecionar={podeEditar || podeDeletar}
      />
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: selecionados.size ? 96 : 0 }}>
      <div>
        <EntityHeader
          title={mobile ? "Suprimentos" : "Cadastro de Suprimentos"}
          subtitle={subtitulo}
          primaryAction={podeCriar ? {
            label: "Novo suprimento", icon: <FiPlus size={14} />, onClick: () => setEditando({ ...NOVO }),
          } : undefined}
          actions={acoesHeader}
          standalone={false}
          sticky={false}
        />
        <KpisCadastro resumo={resumo} ativo={rapido} onFiltrar={setRapido} />
      </div>

      <Toolbar
        buscaRef={buscaRef}
        busca={buscaInput}
        onBusca={setBuscaInput}
        nFiltros={nDimensoes}
        onAbrirFiltros={() => setFiltrosOpen(true)}
        selecao={selecao}
        onRemoverDimensao={(key) => setSelecao((s) => ({ ...s, [key]: null }))}
        onExportar={exportar}
        exportarDesabilitado={!filtrados.length}
        vista={vista}
        onVista={setVista}
        onColunas={() => setPainelColunas(true)}
        mobile={mobile}
      />

      <ChipsRapidos
        rapido={rapido}
        onRapido={setRapido}
        contagens={contagens}
        totalVisivel={filtrados.length}
        resumoFiltro={temFiltro ? resumoFiltro : ""}
        onLimpar={limparFiltros}
      />

      {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

      <BarraMassa
        quantidade={selecionados.size}
        onAcao={(acao) => (acao === "inativar" ? executarLote("inativar") : setAcaoLote(acao))}
        onExcluir={() => setConfirmarLote(true)}
        onLimpar={() => setSelecionados(new Set())}
        podeEditar={podeEditar}
        podeDeletar={podeDeletar}
        podeFornecedor={podeFornecedor}
        executando={executandoLote}
      />
      <ModalLote
        acao={acaoLote}
        quantidade={selecionados.size}
        onConfirmar={(valor) => executarLote(acaoLote, valor)}
        onCancelar={() => setAcaoLote(null)}
      />

      {conteudo()}

      <ConfirmDialog
        open={Boolean(apagando)}
        titulo={`Apagar "${apagando?.codigo}"?`}
        descricao="Só é possível apagar suprimento sem movimentos e sem uso (sobras, peças, reservas, requisições). Nesses casos, desative-o."
        confirmarLabel="Apagar"
        onConfirmar={confirmarApagar}
        onCancelar={() => setApagando(null)}
      />
      <ConfirmDialog
        open={confirmarLote}
        titulo={`Apagar ${selecionados.size} suprimento(s)?`}
        descricao="Suprimento com movimentos ou em uso (sobras, peças, reservas, requisições) não é apagado: ao final você poderá desativar esses no lugar."
        confirmarLabel="Sim, apagar"
        cancelarLabel="Não"
        onConfirmar={excluirSelecionados}
        onCancelar={() => setConfirmarLote(false)}
      />
      <ConfirmDialog
        open={Boolean(recusados)}
        titulo={`${recusados?.length || 0} suprimento(s) em uso não foram apagados`}
        descricao={`${(recusados || []).slice(0, 8).map((r) => `${r.codigo} (${r.motivo})`).join("; ")}${(recusados || []).length > 8 ? "; …" : ""}. Deseja desativar esses suprimentos? Eles somem das listas e ficam com o histórico preservado.`}
        confirmarLabel="Sim, desativar"
        cancelarLabel="Não"
        tone="warning"
        onConfirmar={desativarRecusados}
        onCancelar={() => setRecusados(null)}
      />
      <ProdutoDetalhePanel
        produtoId={detalheId}
        tipos={tiposCatalogo}
        versao={versaoDetalhe}
        onClose={() => setDetalheId(null)}
        onEditar={(p) => { setEditando(paraFormulario(p)); }}
      />
      <ColunasPanel
        open={painelColunas}
        colunas={colunas}
        onClose={() => setPainelColunas(false)}
        onSalvar={salvarColunas}
      />
      <FiltrosPanel
        open={filtrosOpen}
        onClose={() => setFiltrosOpen(false)}
        selecao={selecao}
        onChange={(key, v) => setSelecao((s) => ({ ...s, [key]: v }))}
        opcoes={opcoes}
        totalFiltrado={recorte.length}
        totalGeral={produtos.length}
        onLimpar={() => setSelecao(SELECAO_VAZIA)}
      />

      <FormularioProduto
        editando={editando}
        setEditando={setEditando}
        tiposCatalogo={tiposCatalogo}
        tipoAtual={tipoAtual}
        onSalvar={handleSalvar}
        onCancelar={() => setEditando(null)}
      />
    </div>
  );
}

export default Produtos;
